const KEYWORD_BEFORE_REFERENCE = `((?:CREATE(?:\\s+OR\\s+REPLACE)?|ALTER|END)\\s+\\w+\\s+|OUTPUT\\s+TO\\s+|INPUT\\s+FROM\\s+|FROM\\s+|INTO\\s+|OVER\\s+)`;
const CREATED_ENTITY_PATTERN = /CREATE(?:\s+OR\s+REPLACE)?\s+(\w+)\s+(?:(\w+)\.)?(\w+)/gi;
const APPLICATION_DECLARATION_PATTERN = /CREATE(?:\s+OR\s+REPLACE)?\s+APPLICATION\s+(?:\w+\.)?(\w+)/gi;

export const ENTITY_SUFFIX = "_$ai_scratchpad";

export interface DryCompileEntity {
    type: string;
    name: string;
    // Top-level entity to DROP directly; nested entities are removed via the outer entity's
    // own CASCADE drop, so dropDryCompileEntities skips them.
    isOuter: boolean;
}

interface CreatedEntity {
    type: string;
    name: string;
    namespace: string;
}

// TQL Review Editor may not contain the entire TQL, only what the user wants to create. In these
// cases, the ALTER refers to a real, already-persisted app. Normalizing to CREATE OR REPLACE lets
// the rest of this file treat every snippet uniformly as "declare a brand new (renamed) sibling",
// which must never collide with the real app of the original name.
function normalizeApplicationStatement(tql: string): string {
    return tql
        .replace(/CREATE\s+OR\s+REPLACE\s+APPLICATION/gi, "ALTER APPLICATION")
        .replace(/ALTER\s+APPLICATION\s+/gi, "CREATE OR REPLACE APPLICATION ");
}

function normalizeStandaloneStatement(tql: string): string {
    return tql.replace(/CREATE\s+(?!OR\s+REPLACE\b)(\w+)/gi, "CREATE OR REPLACE $1");
}

function qualifiedKey(namespace: string, name: string): string {
    return namespace ? `${namespace}.${name}` : name;
}

function collectCreatedEntities(tql: string): Map<string, CreatedEntity> {
    const created = new Map<string, CreatedEntity>();
    for (const match of tql.matchAll(CREATED_ENTITY_PATTERN)) {
        const [, type, ns, name] = match;
        const namespace = ns ?? "";
        created.set(qualifiedKey(namespace, name), { type: type.toUpperCase(), name, namespace });
    }
    return created;
}

function redirectEntityReferences(tql: string, created: Map<string, CreatedEntity>): string {
    const pattern = new RegExp(`${KEYWORD_BEFORE_REFERENCE}(?:(\\w+)\\.(\\w+)|(\\w+))`, "gi");
    return tql.replace(pattern, (full, prefix, qualifiedNs, qualifiedName, bareName) => {
        const name = qualifiedName ?? bareName;
        const namespace = qualifiedNs ?? "";
        if (!created.has(qualifiedKey(namespace, name))) return full;

        return `${prefix}${qualifiedKey(namespace, name)}${ENTITY_SUFFIX}`;
    });
}

export function buildDryCompileTQL(tql: string): { tql: string; entities: DryCompileEntity[] } {
    const normalized = normalizeApplicationStatement(tql);
    const appMatches = Array.from(normalized.matchAll(APPLICATION_DECLARATION_PATTERN));
    if (appMatches.length === 0) {
        return { tql: normalizeStandaloneStatement(normalized), entities: [] };
    }

    const created = collectCreatedEntities(normalized);
    const outerNames = new Set(appMatches.map(match => qualifiedKey("", match[1])));
    const entities = Array.from(created.values()).map(({ type, name, namespace }) => ({
        type,
        name: `${qualifiedKey(namespace, name)}${ENTITY_SUFFIX}`,
        isOuter: outerNames.has(qualifiedKey("", name))
    }));

    return { tql: redirectEntityReferences(normalized, created), entities };
}

export async function dropDryCompileEntities(api: any, entities: DryCompileEntity[]): Promise<void> {
    for (const { type, name, isOuter } of entities) {
        if (!isOuter) continue;
        try {
            await api.compileTQL(`DROP ${type} ${name} CASCADE;`, null);
        } catch (err) {
            console.warn(`Failed to drop dry-compile entity ${type} ${name}`, err);
        }
    }
}

export function sanitizeDryCompileEntityError(message: string, entities: DryCompileEntity[]): string {
    return entities.reduce((msg, { name }) => {
        const original = name.slice(0, -ENTITY_SUFFIX.length);
        return msg.split(name).join(original);
    }, message);
}
